import { calculatePercentage, roundNumber } from "@app/shared/utils/math";
import { Avatar } from "@design-system/DataDisplay/Avatar";
import { ProgressBar } from "@design-system/DataDisplay/ProgressBar/ProgressBar";
import { Icon } from "@design-system/Icon";
import { FlexRow } from "@design-system/Layout/Flex";
import {
    CheckboxCell,
    CheckboxHeader,
    type ColumnDef,
} from "@design-system/Table";
import { Text } from "@design-system/Typography/Text";
import dayjs from "dayjs";
import LocalizedFormat from "dayjs/plugin/localizedFormat";
import { type SuppliersListItem } from "../../types";

import { useSetToast } from "@app/components/Toast";
import { TagsMultiSelect } from "@app/shared/components/TagsMultiSelect";
import { Tooltip } from "@design-system/DataDisplay/Tooltip";
import { Button } from "@design-system/Inputs/Button";
import { Chip } from "@design-system/Inputs/Chip";
import { Ellipsis } from "@design-system/Typography/Ellipsis";
import {
    QuestionnaireStatus,
    ResponseStatus,
    TagType,
} from "@generated/client/graphql";
import chroma from "chroma-js";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQueryParam } from "use-query-params";
import { Tag } from "../../../components/Tag";
import { activeFilterParamArgs } from "../../ctx";
import { useSendQuestionnaireEmail } from "../../data";
import { makeSupplierQuestionnaireLink } from "../../services";
import { SendQuestionnaireEmailModal } from "../SendQuestionnaireEmailModal";
import { StatusMultiSelect } from "../StatusMultiSelect";
import { useSupplierTableTranslations } from "./i18n";
import { ProgressSelect } from "./ProgressSelect";
import { QuestionnaireMultiSelect } from "./QuestionnaireMulitSelect";

const checkboxCol: ColumnDef<SuppliersListItem> = {
    id: "checkbox",
    header: ({ table }) => <CheckboxHeader table={table} />,
    cell: ({ row }) => <CheckboxCell row={row} />,
};

const supplierCol: ColumnDef<SuppliersListItem> = {
    accessorKey: "name",
    id: "name",
    meta: {
        cellClassName: "max-w-[250px]",
        headerClassName: "max-w-[250px]",
        colWidth: "250px",
    },
    maxSize: 250,
    header: () => {
        const { t, keys } = useSupplierTableTranslations();
        return (
            <Ellipsis asChild hasTooltip>
                <Text variant="body-sm-bold" color="primary">
                    {t(keys.name)}
                </Text>
            </Ellipsis>
        );
    },
    cell: ({ row }) => {
        return (
            <Link
                className="[all:unset] hover:no-underline"
                to={row.original.id}
            >
                <FlexRow
                    alignItems="center"
                    gap="2.5"
                    className="cursor-pointer group"
                >
                    <FlexRow
                        className="h-6 w-6"
                        justifyContent="center"
                        alignItems="center"
                    >
                        <Avatar
                            size="sm"
                            variant="circle"
                            letter={row.original.name?.[0]}
                            className="h-6 w-6"
                        />
                    </FlexRow>
                    <Ellipsis asChild hasTooltip>
                        <Text
                            variant="body"
                            color="primary"
                            className="group-hover:underline"
                        >
                            {row.original.name}
                        </Text>
                    </Ellipsis>
                </FlexRow>
            </Link>
        );
    },
};

const tagsCol: ColumnDef<SuppliersListItem> = {
    id: "tags",
    meta: {
        cellClassName: "max-w-[200px]",
        headerClassName: "max-w-[200px]",
        colWidth: "200px",
    },
    maxSize: 200,
    header: () => {
        const [tagFilter, setTagFilter] = useQueryParam(
            ...activeFilterParamArgs.tagFilter,
        );
        return (
            <TagsMultiSelect
                tagsIds={tagFilter}
                setTagsIds={setTagFilter}
                type={TagType.Supplier}
                isHeader
            />
        );
    },
    cell: ({ row }) => {
        return (
            <FlexRow alignItems="center" gap="3" className="flex-wrap">
                {row.original.tags.map((tag) => (
                    <Chip key={tag.id} size="sm" interactive={false}>
                        <Chip.Value>{tag.name}</Chip.Value>
                    </Chip>
                ))}
            </FlexRow>
        );
    },
};

const lastQuestionnaireCol: ColumnDef<SuppliersListItem> = {
    id: "lastQuestionnaire",
    meta: {
        cellClassName: "max-w-[200px]",
        headerClassName: "max-w-[200px]",
        colWidth: "200px",
    },
    maxSize: 200,
    header: () => {
        const [questionnaireFilter, setQuestionnaireFilter] = useQueryParam(
            ...activeFilterParamArgs.questionnaireFilter,
        );
        const [noQuestionnaireOnly, setNoQuestionnaireOnly] = useQueryParam(
            ...activeFilterParamArgs.noQuestionnaireOnly,
        );
        return (
            <QuestionnaireMultiSelect
                selectedQuestionnaireIds={
                    questionnaireFilter?.filter(Boolean) as string[]
                }
                onQuestionnaireIdsChange={setQuestionnaireFilter}
                noQuestionnaireOnly={noQuestionnaireOnly}
                onNoQuestionnaireOnlyChange={setNoQuestionnaireOnly}
            />
        );
    },
    cell: ({ row }) => {
        const questionnaire = row.original.lastQuestionnaire;
        const { t, keys } = useSupplierTableTranslations();
        return (
            <Ellipsis asChild hasTooltip>
                <Text
                    variant="body"
                    color={questionnaire ? "primary" : "secondary"}
                >
                    {questionnaire?.name || t(keys.noQuestionnaire)}
                </Text>
            </Ellipsis>
        );
    },
};

const lastQuestionnaireProgressCol: ColumnDef<SuppliersListItem> = {
    id: "lastQuestionnaireProgress",
    meta: {
        cellClassName: "max-w-[150px]",
        headerClassName: "max-w-[150px]",
        colWidth: "150px",
    },
    maxSize: 150,
    header: () => {
        const [progressMin, setProgressMin] = useQueryParam(
            ...activeFilterParamArgs.progressFilter.min,
        );
        const [progressMax, setProgressMax] = useQueryParam(
            ...activeFilterParamArgs.progressFilter.max,
        );
        const setProgress = (progress: { min?: number; max?: number }) => {
            setProgressMin(progress.min);
            setProgressMax(progress.max);
        };

        return (
            <ProgressSelect
                progress={{ min: progressMin, max: progressMax }}
                onProgressChange={setProgress}
            />
        );
    },
    cell: ({ row }) => {
        const questionnaire = row.original.lastQuestionnaire;
        const progress = roundNumber(
            calculatePercentage(
                questionnaire?.stats?.validated || 0,
                questionnaire?.stats?.total || 0,
            ),
        );

        const { t, keys } = useSupplierTableTranslations();

        return (
            <FlexRow alignItems="center" gap="2">
                <ProgressBar
                    segments={[
                        {
                            value: questionnaire?.stats?.answered || 0,
                            label: t(keys.answered_questions),
                            labelValue: `${questionnaire?.stats?.answered || 0} / ${questionnaire?.stats?.total || 0}`,
                            color: chroma("rgba(52, 199, 128, 1)"),
                        },
                        {
                            value: questionnaire?.stats?.validated || 0,
                            label: t(keys.validated_questions),
                            labelValue: `${questionnaire?.stats?.validated || 0} / ${questionnaire?.stats?.total || 0}`,
                            color: chroma("rgba(3, 163, 101, 1)"),
                        },
                    ]}
                    total={questionnaire?.stats?.total || 0}
                    noDataPlaceholder=""
                />
                {questionnaire && (
                    <Text variant="body-sm" color="primary">
                        {progress}%
                    </Text>
                )}
            </FlexRow>
        );
    },
};

const lastQuestionnaireStatusCol: ColumnDef<SuppliersListItem> = {
    id: "lastQuestionnaireStatus",
    header: () => {
        const [statusFilter, setStatusFilter] = useQueryParam(
            ...activeFilterParamArgs.statusFilter,
        );
        return (
            <StatusMultiSelect
                values={statusFilter}
                onValuesChange={setStatusFilter}
            />
        );
    },
    cell: ({ row }) => {
        const questionnaire = row.original.lastQuestionnaire;

        if (
            !questionnaire ||
            questionnaire?.status === QuestionnaireStatus.Unknown
        ) {
            return null;
        }

        const { t, keys } = useSupplierTableTranslations();

        return (
            <Tag
                variant={
                    questionnaire?.status === QuestionnaireStatus.Open
                        ? "success"
                        : "regular"
                }
            >
                {t(keys.status, {
                    context: questionnaire.status,
                })}
            </Tag>
        );
    },
};

const lastUpdatedCol: ColumnDef<SuppliersListItem> = {
    meta: {
        cellClassName: "max-w-[250px]",
        headerClassName: "max-w-[250px]",
        colWidth: "250px",
    },
    minSize: 250,
    id: "lastQuestionnaireUpdatedAt",
    accessorKey: "updatedAt",
    header: () => {
        const { t, keys } = useSupplierTableTranslations();
        return (
            <Text variant="body-sm-bold" color="primary">
                {t(keys.lastUpdated)}
            </Text>
        );
    },
    cell: ({ row }) => {
        const questionnaire = row.original.lastQuestionnaire;
        dayjs.extend(LocalizedFormat);

        const displayDate = questionnaire?.updatedAt
            ? dayjs(questionnaire.updatedAt).format("LL")
            : "-";
        return (
            <FlexRow alignItems="center" gap="2">
                <Text className="text-nowrap" variant="body" color="primary">
                    {displayDate}
                </Text>
                <Icon name="calendar" size="sm" className="text-secondary" />
            </FlexRow>
        );
    },
};

const actionCol: ColumnDef<SuppliersListItem> = {
    id: "action",
    cell: ({ row }) => {
        const { id: supplierId, lastQuestionnaire } = row.original;
        const { org_uname } = useParams();

        const { sendQuestionnaireEmail } = useSendQuestionnaireEmail();

        const { setToastSuccess, setToastError } = useSetToast();

        const { t, keys } = useSupplierTableTranslations();

        const [
            isSendQuestionnaireEmailModalOpen,
            setIsSendQuestionnaireEmailModalOpen,
        ] = useState(false);

        if (!lastQuestionnaire?.id || !org_uname) {
            return null;
        }

        const link = makeSupplierQuestionnaireLink({
            orgUname: org_uname,
            supplierId: supplierId,
            questionnaireId: lastQuestionnaire?.id,
        });

        const handleSendQuestionnaireEmailClick = () => {
            setIsSendQuestionnaireEmailModalOpen(true);
        };

        const handleSendEmailModalConfirmClick = (
            senderName: string,
            emailSubject: string,
            emailText: string,
            emailHtml: string,
            isTest: boolean,
            userIds?: string[],
        ) => {
            sendQuestionnaireEmail(
                {
                    questionnaireId: lastQuestionnaire?.id,
                    email: {
                        senderName: senderName,
                        subject: emailSubject,
                        text: emailText,
                        html: emailHtml,
                    },
                    isTest,
                    userIds,
                },
                {
                    onSettled(data, error) {
                        if (
                            error ||
                            data?.sendSupplierQuestionnaireEmail?.status !==
                                ResponseStatus.Success
                        ) {
                            setToastError(t(keys.send_email_error));
                            return;
                        }
                        setToastSuccess(t(keys.send_email_success));
                    },
                },
            );
        };

        const handleCopyClick = () => {
            navigator.clipboard
                .writeText(link)
                .then(() => {
                    setToastSuccess(t(keys.copy_link_success));
                })
                .catch(() => {
                    setToastError(t(keys.copy_link_error));
                });
        };

        const handleOpenPreview = () => {
            window.open(link, "_blank");
        };

        return (
            <FlexRow alignItems="center" gap="2">
                <Tooltip>
                    <Tooltip.Trigger asChild>
                        <Button
                            size="sm"
                            variant="tonal"
                            onClick={handleOpenPreview}
                        >
                            <Button.Icon name="eyeOn" />
                        </Button>
                    </Tooltip.Trigger>
                    <Tooltip.Content>{t(keys.open_preview)}</Tooltip.Content>
                </Tooltip>
                <Tooltip>
                    <Tooltip.Trigger asChild>
                        <Button
                            size="sm"
                            variant="tonal"
                            onClick={handleSendQuestionnaireEmailClick}
                        >
                            <Button.Icon name="bell" />
                        </Button>
                    </Tooltip.Trigger>
                    <Tooltip.Content>{t(keys.send_email)}</Tooltip.Content>
                </Tooltip>
                <Tooltip>
                    <Tooltip.Trigger asChild>
                        <Button
                            size="sm"
                            variant="tonal"
                            onClick={handleCopyClick}
                        >
                            <Button.Icon name="link" />
                        </Button>
                    </Tooltip.Trigger>
                    <Tooltip.Content>{t(keys.copy_link)}</Tooltip.Content>
                </Tooltip>
                <SendQuestionnaireEmailModal
                    open={isSendQuestionnaireEmailModalOpen}
                    onOpenChange={setIsSendQuestionnaireEmailModalOpen}
                    onSendClick={handleSendEmailModalConfirmClick}
                    supplierUsers={row.original.users}
                />
            </FlexRow>
        );
    },
};

export const useColumns = (): ColumnDef<SuppliersListItem>[] => {
    const columns = useMemo(
        () => [
            checkboxCol,
            supplierCol,
            tagsCol,
            lastQuestionnaireCol,
            lastQuestionnaireProgressCol,
            lastQuestionnaireStatusCol,
            lastUpdatedCol,
            actionCol,
        ],
        [],
    );
    return columns;
};
